import { Platform, StyleSheet, View } from "react-native";

import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  DetailRow,
  MetricCard,
  ProtectedScreen,
  SectionHeader,
  Stack,
} from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import { usePlan } from "~/hooks/usePlan";
import { usePurchases } from "~/hooks/usePurchases";
import { openExternalUrl } from "~/utils/external-link";
import { notify } from "~/utils/notify";
import { PRO_PRICE_TEXT } from "~/utils/plan-limits";
import { spacing } from "~/styles/theme";

const PRO_FEATURES = [
  "Unlimited gear items",
  "Receipt and photo vault",
  "Custom spec fields",
  "Warranty and service reminders",
];

export default function SubscriptionScreen() {
  const { user, refresh } = useAuth();
  const plan = usePlan();
  const purchases = usePurchases(user?.id ?? null);

  async function manage() {
    try {
      if (purchases.supportsCustomerCenter && Platform.OS !== "web") {
        await purchases.presentCustomerCenter();
        await refresh();
        return;
      }
      const url = await purchases.getManagementUrl();
      if (url) {
        await openExternalUrl(url);
        return;
      }
      notify(
        "Manage your subscription",
        "Use the App Store or Google Play subscription settings on the device you purchased with.",
      );
    } catch (error) {
      notify(
        "Unable to open subscription management",
        error instanceof Error ? error.message : "Please try again.",
      );
    }
  }

  return (
    <ProtectedScreen
      narrow
      title={plan.isPro ? "Your subscription" : "GearSync Pro"}
      subtitle={
        plan.isPro
          ? "Thanks for supporting GearSync."
          : `Everything unlocked for ${PRO_PRICE_TEXT} a month.`
      }
    >
      <Card gap="md" accent="mint">
        <View style={stylesheet.header}>
          <SectionHeader title={plan.isPro ? "Pro" : "Free"} />
          <Badge tone={plan.isPro ? "success" : "neutral"}>
            {plan.isPro ? "Active" : "No subscription"}
          </Badge>
        </View>

        <View style={stylesheet.metrics}>
          <View style={stylesheet.metric}>
            <MetricCard
              label="Items"
              value={
                plan.items.limit === null
                  ? `${plan.items.used}`
                  : `${plan.items.used} / ${plan.items.limit}`
              }
            />
          </View>
        </View>

        <Stack gap="xs">
          {PRO_FEATURES.map((feature) => (
            <DetailRow
              key={feature}
              label={feature}
              value={plan.isPro ? "Included" : "Pro"}
            />
          ))}
        </Stack>
      </Card>

      {plan.isPro ? (
        <Card gap="sm">
          <SectionHeader
            title="Manage"
            description="Change or cancel your plan through the store you purchased with."
          />
          <Button
            align="start"
            variant="outline"
            disabled={purchases.managing}
            onPress={() => void manage()}
          >
            <ButtonText>
              {purchases.managing ? "Opening..." : "Manage subscription"}
            </ButtonText>
          </Button>
        </Card>
      ) : (
        <UpgradePrompt />
      )}

      <Card gap="sm">
        <SectionHeader
          title="Free forever"
          description="Ten gear items, unlimited kits, basic specs, and purchase dates. Free forever — the 14-day trial is for Pro."
        />
      </Card>
    </ProtectedScreen>
  );
}

const stylesheet = StyleSheet.create({
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.m,
  },
  metrics: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
  metric: { minWidth: 160, flex: 1 },
});
